import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import LoginPage from '../../features/auth/LoginPage';
import ForgotPasswordPage from '../../features/auth/ForgotPasswordPage';
import ResetPasswordPage from '../../features/auth/ResetPasswordPage';
import AppLayout from '../layout/AppLayout';
import ErrorBoundary from '../layout/ErrorBoundary';
import DashboardPage from '../../features/dashboard/DashboardPage';
import NotFoundPage from '../pages/NotFoundPage';
import ProtectedRoute from './ProtectedRoute';

/**
 * The whole route table, in one file.
 *
 * Everything except /login sits behind ProtectedRoute -> AppLayout, so a new
 * page is one <Route> line and cannot accidentally ship unauthenticated.
 *
 * **Feature screens are loaded on demand.** Every page used to be imported
 * eagerly, which meant one bundle carrying partner management, client
 * management, security parameters, QR templates and feature management — all
 * downloaded before the login box could be drawn, by someone who had not signed
 * in yet. `lazy()` gives each screen its own file, fetched the first time it is
 * visited and cached after that. `AppLayout` holds the <Suspense> that covers
 * the wait, so the sidebar and topbar stay on screen while a page arrives.
 *
 * Four things stay eager on purpose:
 *   - **LoginPage** is the first thing an unauthenticated visitor needs; making
 *     it a second round trip would delay the only screen they can see.
 *   - **DashboardPage** is where every successful login lands, immediately.
 *   - **NotFoundPage** and the layout are small and needed everywhere.
 */
const AccountPage = lazy(() => import('../../features/auth/AccountPage'));
const AuditLogPage = lazy(() => import('../../features/auditLog/AuditLogPage'));
const RequestLogPage = lazy(() => import('../../features/auditLog/RequestLogPage'));
const PartnerListPage = lazy(() => import('../../features/partnerManagement/PartnerListPage'));
const PartnerOnboardForm = lazy(() => import('../../features/partnerManagement/PartnerOnboardForm'));
const PartnerTypeConfig = lazy(() => import('../../features/partnerManagement/PartnerTypeConfig'));
const ClientListPage = lazy(() => import('../../features/clientManagement/ClientListPage'));
const ClientOnboardForm = lazy(() => import('../../features/clientManagement/ClientOnboardForm'));
const ClientMappingPage = lazy(() => import('../../features/clientManagement/ClientMappingPage'));
const SecurityConfigPage = lazy(() => import('../../features/securityParameters/SecurityConfigPage'));
const QrTemplateListPage = lazy(() => import('../../features/qrTemplates/QrTemplateListPage'));
const QrTemplateUploadForm = lazy(() => import('../../features/qrTemplates/QrTemplateUploadForm'));
const FeatureTogglePanel = lazy(() => import('../../features/featureManagement/FeatureTogglePanel'));

export default function AppRoutes() {
  return (
    /* The outer net. AppLayout has its own boundary around the routed page,
       which keeps the sidebar usable when one screen fails; this one is for the
       rarer case where the shell itself — or the login screen — is what threw,
       and there is no shell left to render a message inside. */
    <ErrorBoundary>
      <Routes>
        {/* The three signed-out screens. Eager, like LoginPage: someone
            following a reset link from their inbox should not wait on a second
            round trip, and these are small. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/audit-log/requests" element={<RequestLogPage />} />

            <Route path="/partners" element={<PartnerListPage />} />
            <Route path="/partners/new" element={<PartnerOnboardForm />} />
            <Route path="/partners/types" element={<PartnerTypeConfig />} />
            {/* Same component as /partners/new — PROJECT_SPEC section 4.2 gives
                PartnerOnboardForm both jobs, and the backend edits a partner by
                id (GET/PATCH /:id), so editing needs its own addressable URL. */}
            <Route path="/partners/:id/edit" element={<PartnerOnboardForm />} />

            <Route path="/clients" element={<ClientListPage />} />
            <Route path="/clients/new" element={<ClientOnboardForm />} />
            {/* Same component as /clients/new — the backend edits a client by id
                (GET/PATCH /:id), so editing needs its own addressable URL, same
                reasoning as /partners/:id/edit above. */}
            <Route path="/clients/:id/edit" element={<ClientOnboardForm />} />
            {/* With no :id, ClientMappingPage renders its own client picker. */}
            <Route path="/clients/mapping" element={<ClientMappingPage />} />
            <Route path="/clients/:id/mapping" element={<ClientMappingPage />} />

            {/* With no :clientId, SecurityConfigPage renders its own client
                picker — thresholds are per program, same reasoning as
                /clients/mapping above. */}
            <Route path="/security-parameters" element={<SecurityConfigPage />} />
            <Route path="/security-parameters/:clientId" element={<SecurityConfigPage />} />

            {/* With no :clientId, QrTemplateListPage renders its own client
                picker — designs are per program, same reasoning as
                /security-parameters above. */}
            <Route path="/qr-templates" element={<QrTemplateListPage />} />
            <Route path="/qr-templates/upload" element={<QrTemplateUploadForm />} />
            <Route path="/qr-templates/:clientId" element={<QrTemplateListPage />} />

            {/* With no :tenantId, FeatureTogglePanel renders its own client
                picker — feature access is per program, same reasoning as
                /security-parameters above. /features/catalog is the global
                catalog; React Router ranks the static segment above the dynamic
                one, so it never reaches the :tenantId route. */}
            <Route path="/features" element={<FeatureTogglePanel />} />
            <Route path="/features/catalog" element={<FeatureTogglePanel />} />
            <Route path="/features/:tenantId" element={<FeatureTogglePanel />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </ErrorBoundary>
  );
}
